import { Controller } from "@hotwired/stimulus"
import { areaStore } from "../../stores/locales/area_store"
import { buildingStore } from "../../stores/locales/building_store"
import { useStore } from "stimulus-store"
import { getArea } from "shared/locales/locales"
import { getCsrfToken } from "shared/form_helpers"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"

// Connects to data-controller="forms--area-form"
export default class extends Controller {

    static stores = [
        areaStore,
        buildingStore
    ]

    static targets = [
        "nameInput",
        "floorInput",
        "managerInput",
        "floorPlanInput"
    ]

    static values = {
        path: String,
        method: String
    }

    connect() {
        useStore(this)

        // Extract area ID from URL and populate area store
        this.initializeAreaFromUrl()

        // Don't populate form on initial connect - let Rails presenters handle initial values
        // The form will be updated when area store changes via onAreaStoreUpdate
    }

    /**
     * React to area store changes and populate the form
     */
    onAreaStoreUpdate(area) {
        if (area && area.id) {
            this.populateForm(area)
        } else {
            this.clearForm()
        }
    }

    /**
     * Populate form inputs with area data
     */
    populateForm(area) {
        // Populate name field
        if (this.hasNameInputTarget && area.name) {
            this.nameInputTarget.value = area.name
        }

        // Populate floor field
        if (this.hasFloorInputTarget && area.floor) {
            this.floorInputTarget.value = area.floor
        }

        // Populate manager field
        if (this.hasManagerInputTarget && area.manager) {
            this.managerInputTarget.value = area.manager
        }

        // Handle floor plan input - this is a file input so we can't set its value
        // but we can update the label to show the current floor plan name
        if (this.hasFloorPlanInputTarget && area.floor_plan_url) {
            this.updateFloorPlanLabel(area.floor_plan_url)
        }
    }

    /**
     * Clear all form inputs
     */
    clearForm() {
        if (this.hasNameInputTarget) {
            this.nameInputTarget.value = ''
        }

        if (this.hasFloorInputTarget) {
            this.floorInputTarget.value = ''
        }

        if (this.hasManagerInputTarget) {
            this.managerInputTarget.value = ''
        }

        if (this.hasFloorPlanInputTarget) {
            this.clearFloorPlanLabel()
        }
    }

    /**
     * Update the floor plan file input label to show current floor plan
     */
    updateFloorPlanLabel(floorPlanUrl) {
        // Find the custom file label associated with the floor plan input
        const label = this.element.querySelector('.custom-file-label')
        if (label) {
            // Extract filename from URL or show generic message
            const filename = this.extractFilenameFromUrl(floorPlanUrl)
            label.textContent = filename || 'Current floor plan'
            label.classList.add('selected')
        }
    }

    /**
     * Clear the floor plan file input label
     */
    clearFloorPlanLabel() {
        const label = this.element.querySelector('.custom-file-label')
        if (label) {
            label.textContent = 'Choose file...'
            label.classList.remove('selected')
        }
    }

    /**
     * Extract filename from a URL
     */
    extractFilenameFromUrl(url) {
        try {
            const urlObj = new URL(url)
            const pathname = urlObj.pathname
            const filename = pathname.split('/').pop()
            return filename || null
        } catch (error) {
            console.warn('Could not extract filename from URL:', url)
            return null
        }
    }

    /**
     * Initialize area store from URL area ID
     */
    async initializeAreaFromUrl() {
        const areaId = this.extractAreaIdFromUrl()

        if (areaId) {
            // Use the shared getArea method and set the store with a Promise
            this.setAreaStoreValue(Promise.resolve(getArea(areaId)))
        }
    }

    /**
     * Extract area ID from the current URL
     * URL format: /areas/edit/{area-id}/edit
     */
    extractAreaIdFromUrl() {
        const path = window.location.pathname
        const match = path.match(/\/areas\/edit\/([^\/]+)\/edit/)
        return match ? match[1] : null
    }

    async saveArea() {
        const isCreate = this.methodValue === 'POST'
        const isUpdate = this.methodValue === 'PATCH'

        // Build form data based on operation type
        const formData = {
            name: this.hasNameInputTarget ? this.nameInputTarget.value : null,
            floor: this.hasFloorInputTarget ? this.floorInputTarget.value : null,
            manager: this.hasManagerInputTarget ? this.managerInputTarget.value : null
        }

        // Add operation-specific fields
        if (isCreate) {
            // For create, get parent_id from buildingStore
            const building = this.buildingStoreValue
            const parentId = building?.id || null

            if (!parentId) {
                console.error('No building selected for area creation')
                sendFailureNotification('Please select a building before creating an area.')
                return
            }

            formData.parent_id = parentId
        } else if (isUpdate) {
            // For update, get id from areaStore
            const area = this.areaStoreValue

            if (!area || !area.id) {
                console.error('No area ID available for update')
                sendFailureNotification('Unable to save area. Please try again.')
                return
            }

            formData.id = area.id
        }

        // Handle floor plan file if present
        if (this.hasFloorPlanInputTarget && this.floorPlanInputTarget.files.length > 0) {
            formData.floor_plan = this.floorPlanInputTarget.files[0]
        }

        try {
            // Prepare request body
            // If there's a file, use FormData, otherwise use JSON
            let body
            let headers

            if (formData.floor_plan) {
                // Use FormData for file upload
                body = new FormData()

                // Add all form fields to FormData
                if (isCreate && formData.parent_id) {
                    body.append('parent_id', formData.parent_id)
                }
                if (isUpdate && formData.id) {
                    body.append('id', formData.id)
                }
                if (formData.name) {
                    body.append('name', formData.name)
                }
                if (formData.floor) {
                    body.append('floor', formData.floor)
                }
                if (formData.manager) {
                    body.append('manager', formData.manager)
                }
                body.append('floor_plan', formData.floor_plan)

                // For FormData, set Accept and CSRF token headers
                // Let browser set Content-Type with proper boundary
                headers = {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            } else {
                // Use JSON for regular data
                headers = {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
                body = JSON.stringify(formData)
            }

            // Construct URL using pathValue
            const url = `/api/locales/areas/${ this.pathValue }`

            const response = await fetch(url, {
                method: this.methodValue,
                headers: headers,
                body: body
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                const message = errorData.message || `HTTP ${ response.status }: ${ response.statusText }`
                throw new Error(message)
            }

            // Parse the JSON response
            const data = await response.json()

            // Extract area name from response
            const areaName = data.name || formData.name || 'Area'
            const actionVerb = isCreate ? 'created' : 'updated'

            // Show success notification
            sendSuccessNotification(`Successfully ${ actionVerb } ${ areaName }`)

            // Update the area store with the new data
            if (data) {
                this.setAreaStoreValue(data)
            }

            // For create operations, redirect to the edit page
            if (isCreate && data.id) {
                window.location.href = `/areas/edit/${ data.id }/edit`
            }

        } catch (error) {
            console.error('Error saving area:', error)

            // Show failure notification
            const actionVerb = isCreate ? 'create' : 'save'
            sendFailureNotification(`Unable to ${ actionVerb } area. ${ error.message }`)
        }
    }

    async deleteArea() {
        // Get the area ID from the store
        const area = this.areaStoreValue

        if (!area || !area.id) {
            return
        }

        const areaId = area.id
        const areaName = area.name || 'Area'

        // Show confirmation modal
        if (!window.confirm(`Are you sure you want to delete ${ areaName }? All devices will be removed.`)) {
            return
        }

        try {
            const response = await fetch('/api/locales/areas/delete', {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({
                    id: areaId
                })
            })

            if (response.ok) {
                // Parse the JSON response
                const data = await response.json()

                // Show success notification
                sendSuccessNotification(`Successfully deleted ${ areaName }`)

                // Remove this area from the allAreasStore and clear out the areaStore value
                this.setAreaStoreValue({})

                // Redirect to areas update page
                this.redirectBackToAreaSelectPage();
            } else {
                sendFailureNotification(`Unable to delete ${ areaName }. Please try again.`)
            }
        } catch (error) {
            // Show failure notification and stay on the same page
            sendFailureNotification(`Unable to delete ${ areaName }. Please try again.`)
        }
    }

    redirectBackToAreaSelectPage() {
        // Redirect to areas update page
        window.location.href = '/areas/update.html'
    }
}